/* eslint-disable no-console */
import { environment } from '../../environments/environment';

interface SDKAnalyticsConfig {
	gtm: string;
	use_sessionStorage: boolean;
	observability: {
		env: string;
		name: string;
		version: string;
		sigla: string;
		externalApplication: boolean;
		datadog: {
			logs: {
				clientToken: string;
			},
			rum: {
				applicationId: string;
				clientToken: string;
			},
		},
	},
	fullstory: {
		organizationID: string;
		sampling: number;
	};
}

interface ISDKAnalytics {
	setUserProperties(properties: Record<string, unknown>): void;
	setDefaultParameters(parameters: Record<string, unknown>): void;
	track(event: Record<string, unknown>): void;
}

interface SDKAnalyticsWindow extends Window {
	SDKAnalytics?: (config: SDKAnalyticsConfig) => ISDKAnalytics;
}

interface AnalyticsConfigurator {
	use: () => ISDKAnalytics;
	apply: () => Promise<boolean>;
}

const NULL_SDK: ISDKAnalytics = {
	setUserProperties: () => void 0,
	setDefaultParameters: () => void 0,
	track: () => void 0,
};

const createConfigurator = (): AnalyticsConfigurator => {
	let sdk: ISDKAnalytics = NULL_SDK;

	const use = (): ISDKAnalytics => {
		return sdk;
	};

	const apply = (): Promise<boolean> => {
		return new Promise((resolve) => {
			const script = document.createElement('script');
			script.src = environment.sdkAnalyticsUrl;
			script.async = true;

			script.onload = () => {
				try {
					setPlatform();
					resolve(true);
				} catch (error) {
					console.error('Error: Failed to apply SDK Analytics configuration.', error);
					resolve(false);
				}
			};

			script.onerror = (error) => {
				console.error('Error: Could not load SDK Analytics script.', error);
				resolve(false);
			};

			document.head.appendChild(script);
		});
	};

	const setPlatform = (): void => {
		const sdkWindow = window as SDKAnalyticsWindow;

		if (!sdkWindow.SDKAnalytics) {
			throw new Error('SDKAnalytics is not available on window object');
		}

		sdk = sdkWindow.SDKAnalytics({
			gtm: environment.googleTagManagerId,
			use_sessionStorage: true,
			observability: environment.dataDog,
			fullstory: {
				organizationID: environment.fullstoryId,
				sampling: 1,
			},
		});
	};

	return {
		use,
		apply,
	};
};

const instance = createConfigurator();

export default instance;
